import { Component, OnInit, OnDestroy, ElementRef, viewChildren, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '../../../services/project.service';
import { ProjectContextService } from '../../../services/project-context.service';
import { GroupService } from '../../../services/group.service';
import { ProfileService } from '../../../services/profile.service';
import { ToastService } from '../../../services/toast.service';
import { Project } from '../../../dtos/project';
import { ProjectProtocol } from '../../../dtos/project-protocol';
import { Group } from '../../../dtos/group';
import { UserInfo } from '../../../dtos/user-info';
import { Member } from '../../../dtos/member';
import { ProjectCreateUpdate } from '../../../dtos/project-create-update';
import { AddResearcherModalComponent } from '../modals/add-researcher-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { BaseAlertComponent } from '../../alerts/base-alert/base-alert.component';
import { ProtocolCardComponent } from '../protocol-card/protocol-card.component';
import { InfoIconComponent } from '../project-guide/info-icon/info-icon.component';
import { HttpQuestionnaireAdapter } from '../../../extraction-schema/adapters/http-questionnaire.adapter';
import { formatStageState, getStageStateClass, ProjectState } from '../../../dtos/project-state';

const DEBOUNCE_MS = 300;
// size for researchers and locks pagination
const PAGE_SIZE = 8;

@Component({
  selector: 'app-project-detail',
  standalone: true,
  imports: [
    CommonModule,
    AddResearcherModalComponent,
    FormsModule,
    BaseAlertComponent,
    ConfirmModalComponent,
    ProtocolCardComponent,
    InfoIconComponent,
  ],
  templateUrl: './project-detail.component.html',
  styleUrls: ['./project-detail.component.scss'],
})
export class ProjectDetailComponent implements OnInit, OnDestroy {
  projectId: string = '';
  project: Project | null = null;
  group: Group | null = null;
  groupMembers: Member[] = [];
  projectResearchers = signal<any[]>([]);
  projectResearchersTotalItems = signal(0);
  projectResearchersTotalPages = signal(0);
  projectResearchersCurrentPage = signal(0);
  researcherSearchQuery = signal('');
  userInfo: UserInfo | null = null;

  exclusionReasons: string[] = [];
  editableExclusionReasons: string[] = [];
  isEditingReasons = false;
  warningMessage: string | null = null;
  showRemoveReasonModal = false;
  reasonPendingRemovalIndex: number | null = null;
  extractionQuestionCount = 0;
  agreementThresholdInput: number | string | null = null;
  agreementThresholdError = '';
  savingAgreementThreshold = false;

  inclusionCriteriaList: string[] = [];
  exclusionCriteriaList: string[] = [];
  editableInclusionCriteria: string[] = [];
  editableExclusionCriteria: string[] = [];
  isEditingCriteria = false;
  savingCriteria = false;
  criteriaError = '';

  protocol: ProjectProtocol | null = null;
  savingProtocol = false;

  loading = true;
  showAddResearcherModal = false;
  showRemoveConfirmModal = false;
  researcherToRemove: any = null;
  removingResearcher = false;

  projectLocks = signal<any[]>([]);
  projectLocksTotalItems = signal(0);
  projectLocksTotalPages = signal(0);
  projectLocksCurrentPage = signal(0);
  lockSearchQuery = signal('');
  showForceUnlockConfirmModal = false;
  lockToForceUnlock: any = null;
  releasingLock = false;

  collapsedSections = signal<Record<string, boolean>>({});

  toggleSection(name: string): void {
    this.collapsedSections.update((s) => ({ ...s, [name]: !s[name] }));
  }

  isCollapsed(name: string): boolean {
    return !!this.collapsedSections()[name];
  }

  private sub?: Subscription;
  private readonly researcherSearch$ = new Subject<{ q: string; page: number }>();
  private readonly lockSearch$ = new Subject<{ q: string; page: number }>();
  readonly reasonInputs = viewChildren<ElementRef<HTMLInputElement>>('reasonInput');
  readonly criteriaInputs = viewChildren<ElementRef<HTMLInputElement>>('criteriaInput');
  readonly inclusionInputs = viewChildren<ElementRef<HTMLTextAreaElement>>('inclusionInput');

  readonly exclusionInputs = viewChildren<ElementRef<HTMLTextAreaElement>>('exclusionInput');
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService,
    private ctx: ProjectContextService,
    private groupService: GroupService,
    private profileService: ProfileService,
    private toastService: ToastService,
    private questionnaireAdapter: HttpQuestionnaireAdapter,
  ) {}

  ngOnInit(): void {
    this.sub = this.route.parent?.params.subscribe((params) => {
      this.projectId = params['projectId'];
      if (!this.projectId) {
        this.router.navigate(['/projects']);
        return;
      }
      this.loadData();
    });
    this.sub?.add(
      this.projectService.projectUpdated$.subscribe(() => {
        this.loadData();
      }),
    );
    this.loadUserInfo();

    const researcherSub = this.researcherSearch$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, page }) => {
          this.projectResearchersCurrentPage.set(page);
          return this.projectService.getProjectResearchers(
            this.projectId,
            page,
            PAGE_SIZE,
            q || undefined,
          );
        }),
      )
      .subscribe({
        next: (res) => {
          this.projectResearchers.set(res.items);
          this.projectResearchersTotalItems.set(res.totalItems);
          this.projectResearchersTotalPages.set(res.totalPages);
          this.projectResearchersCurrentPage.set(res.currentPage);
        },
        error: (err) => {
          console.error('Error loading project researchers:', err);
        },
      });
    this.sub?.add(researcherSub);

    const lockSub = this.lockSearch$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, page }) => {
          this.projectLocksCurrentPage.set(page);
          return this.projectService.getProjectLocks(
            this.projectId,
            page,
            PAGE_SIZE,
            q || undefined,
          );
        }),
      )
      .subscribe({
        next: (res) => {
          this.projectLocks.set(res.items);
          this.projectLocksTotalItems.set(res.totalItems);
          this.projectLocksTotalPages.set(res.totalPages);
          this.projectLocksCurrentPage.set(res.currentPage);
        },
        error: (err) => {
          console.error('Error loading project locks:', err);
        },
      });
    this.sub?.add(lockSub);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private loadData(): void {
    this.loading = true;

    // Load exclusion reasons
    const reasonsSub = this.projectService.getExclusionReasons(this.projectId).subscribe({
      next: (reasons) => {
        this.exclusionReasons = reasons;
      },
      error: (err) => {
        console.error('Error loading exclusion reasons:', err);
      },
    });
    this.sub?.add(reasonsSub);

    // Load project details
    const projectSub = this.projectService.getProjectById(this.projectId).subscribe({
      next: (project) => {
        this.project = project;
        this.inclusionCriteriaList = this.parseCriteriaList(project.inclusionCriteria);
        this.exclusionCriteriaList = this.parseCriteriaList(project.exclusionCriteria);
        this.resetAgreementThresholdInput(project);
        if (this.isProjectPI()) {
          this.loadProjectLocks();
        }
        // Load group information
        const groupSub = this.groupService.getGroupById(project.groupId).subscribe({
          next: (group) => {
            this.group = group;
            // Load group members
            this.loadGroupMembers();
          },
          error: (err) => {
            console.error('Error loading group:', err);
            this.loading = false;
          },
        });
        this.sub?.add(groupSub);
      },
      error: (err) => {
        console.error('Error loading project:', err);
        this.loading = false;
      },
    });
    this.sub?.add(projectSub);

    // Load project researchers
    this.loadProjectResearchers();

    // Load project protocol
    const protocolSub = this.projectService.getProjectProtocol(this.projectId).subscribe({
      next: (protocol) => {
        this.protocol = protocol;
      },
      error: (err) => {
        console.error('Error loading protocol:', err);
      },
    });
    this.sub?.add(protocolSub);

    // Load extraction question count
    const qSub = this.questionnaireAdapter.getQuestions(this.projectId).subscribe({
      next: (questions) => {
        this.extractionQuestionCount = questions.length;
      },
      error: () => {
        this.extractionQuestionCount = 0;
      },
    });
    this.sub?.add(qSub);
  }

  private loadGroupMembers(): void {
    if (!this.project) return;

    const membersSub = this.groupService.getAllGroupMembers(this.project.groupId).subscribe({
      next: (members) => {
        this.groupMembers = members;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading group members:', err);
        this.loading = false;
      },
    });
    this.sub?.add(membersSub);
  }

  private loadProjectResearchers(page: number = 0, q?: string): void {
    const researchersSub = this.projectService
      .getProjectResearchers(this.projectId, page, PAGE_SIZE, q)
      .subscribe({
        next: (res) => {
          this.projectResearchers.set(res.items);
          this.projectResearchersTotalItems.set(res.totalItems);
          this.projectResearchersTotalPages.set(res.totalPages);
          this.projectResearchersCurrentPage.set(res.currentPage);
        },
        error: (err) => {
          console.error('Error loading project researchers:', err);
        },
      });
    this.sub?.add(researchersSub);
  }

  private loadUserInfo(): void {
    const userSub = this.profileService.getCurrentUser().subscribe({
      next: (user) => {
        this.userInfo = user;
        if (this.isProjectPI()) {
          this.loadProjectLocks();
        }
      },
      error: (err) => {
        console.error('Error loading user profile:', err);
      },
    });
    this.sub?.add(userSub);
  }

  openAddResearcherModal(): void {
    this.showAddResearcherModal = true;
  }

  onResearcherAdded(_: any): void {
    this.loadProjectResearchers(this.projectResearchersCurrentPage());
  }

  onResearcherSearchChange(value: string): void {
    this.researcherSearchQuery.set(value);
    this.projectResearchersCurrentPage.set(0);
    this.researcherSearch$.next({ q: value, page: 0 });
  }

  onLockSearchChange(value: string): void {
    this.lockSearchQuery.set(value);
    this.projectLocksCurrentPage.set(0);
    this.lockSearch$.next({ q: value, page: 0 });
  }

  goToResearchersPage(page: number): void {
    this.researcherSearch$.next({ q: this.researcherSearchQuery(), page });
  }

  goToLocksPage(page: number): void {
    this.lockSearch$.next({ q: this.lockSearchQuery(), page });
  }

  openRemoveConfirm(researcher: any): void {
    this.researcherToRemove = researcher;
    this.showRemoveConfirmModal = true;
  }

  confirmRemoveResearcher(): void {
    if (!this.researcherToRemove || !this.project) {
      return;
    }

    this.removingResearcher = true;
    const researcherId = this.researcherToRemove.userId || this.researcherToRemove.id;
    const researcherName = `${this.researcherToRemove.firstName} ${this.researcherToRemove.lastName || this.researcherToRemove.fullName}`;

    this.projectService.removeResearcherFromProject(this.project.id, researcherId).subscribe({
      next: () => {
        this.removingResearcher = false;
        this.showRemoveConfirmModal = false;
        this.researcherToRemove = null;
        this.loadProjectResearchers();
        this.toastService.success(
          'Researcher Removed',
          `${researcherName} has been removed from the project`,
        );
      },
      error: (err) => {
        console.error('Error removing researcher:', err);
        this.removingResearcher = false;
        const errorMessage =
          typeof err?.error === 'string'
            ? err.error
            : err?.error?.message || 'Could not remove researcher. Please try again.';
        this.toastService.error('Failed to Remove Researcher', errorMessage);
      },
    });
  }

  canManageProject(): boolean {
    if (!this.project || !this.userInfo) {
      return false;
    }
    return this.isSameUser(this.project.piUserId, this.userInfo.id);
  }

  openExtractionQuestions(): void {
    this.ctx.openExtractionQuestions();
  }

  getAvailableMembers(): Member[] {
    // Return group members that are not already researchers on the project
    return this.groupMembers.filter(
      (member) =>
        !this.projectResearchers().some((researcher) =>
          this.isSameUser(member.user.id, researcher.userId || researcher.id),
        ),
    );
  }

  private isSameUser(userId1: any, userId2: any): boolean {
    return String(userId1) === String(userId2);
  }

  isPiUser(researcher: any): boolean {
    if (!this.project) return false;
    return String(this.project.piUserId) === String(researcher.userId || researcher.id);
  }

  isProjectPI(): boolean {
    if (!this.project || !this.userInfo) {
      return false;
    }
    return this.isSameUser(this.project.piUserId, this.userInfo.id);
  }

  isProtocolLocked(): boolean {
    return this.project?.state !== 'SETUP' && this.project?.state !== 'INGESTION';
  }

  canEditCriteria(): boolean {
    if (!this.project) return false;
    return this.isProjectPI() && !this.isProtocolLocked();
  }

  private parseCriteriaList(val: string | undefined | null): string[] {
    if (!val) return [];
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return val
        .split('\n')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
  }

  private serializeCriteriaList(list: string[]): string | undefined {
    const cleaned = list.map((s) => s.trim()).filter((s) => s.length > 0);
    return cleaned.length > 0 ? JSON.stringify(cleaned) : undefined;
  }

  startEditingCriteria(): void {
    this.editableInclusionCriteria = [...this.inclusionCriteriaList];
    this.editableExclusionCriteria = [...this.exclusionCriteriaList];
    this.isEditingCriteria = true;
    this.criteriaError = '';
    this.addInclusionCriterion();
    this.focusInclusionInput(this.editableInclusionCriteria.length - 1);
  }

  cancelEditingCriteria(): void {
    this.isEditingCriteria = false;
    this.criteriaError = '';
  }

  addInclusionCriterion(): number {
    this.editableInclusionCriteria.push('');
    return this.editableInclusionCriteria.length - 1;
  }

  addExclusionCriterion(): number {
    this.editableExclusionCriteria.push('');
    return this.editableExclusionCriteria.length - 1;
  }

  removeInclusionCriterion(index: number): void {
    this.editableInclusionCriteria.splice(index, 1);
  }

  removeExclusionCriterion(index: number): void {
    this.editableExclusionCriteria.splice(index, 1);
  }

  onInclusionKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== 'Enter') return;
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      this.saveCriteria();
      return;
    }
    if (event.shiftKey) return;
    event.preventDefault();
    const newIndex = this.addInclusionCriterion();
    this.focusInclusionInput(newIndex);
  }

  onExclusionKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== 'Enter') return;
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      this.saveCriteria();
      return;
    }
    if (event.shiftKey) return;
    event.preventDefault();
    const newIndex = this.addExclusionCriterion();
    this.focusExclusionInput(newIndex);
  }

  private focusInclusionInput(index: number): void {
    setTimeout(() => {
      const inputs = this.inclusionInputs();
      if (inputs[index]) {
        inputs[index].nativeElement.focus();
      }
    });
  }

  private focusExclusionInput(index: number): void {
    setTimeout(() => {
      const inputs = this.exclusionInputs();
      if (inputs[index]) {
        inputs[index].nativeElement.focus();
      }
    });
  }

  trackByIndex(i: number): number {
    return i;
  }

  saveCriteria(): void {
    if (!this.project || this.savingCriteria) {
      return;
    }

    this.savingCriteria = true;
    this.criteriaError = '';

    const request: ProjectCreateUpdate = {
      groupId: this.project.groupId,
      title: this.project.title,
      description: this.project.description || undefined,
      exclusionCriteria: this.serializeCriteriaList(this.editableExclusionCriteria),
      inclusionCriteria: this.serializeCriteriaList(this.editableInclusionCriteria),
    };

    this.projectService.updateProject(this.project.id, request).subscribe({
      next: (updatedProject) => {
        this.project = updatedProject;
        this.inclusionCriteriaList = this.parseCriteriaList(updatedProject.inclusionCriteria);
        this.exclusionCriteriaList = this.parseCriteriaList(updatedProject.exclusionCriteria);
        this.isEditingCriteria = false;
        this.savingCriteria = false;
        this.toastService.success(
          'Criteria Saved',
          'Screening criteria have been updated successfully.',
        );
      },
      error: (err) => {
        this.savingCriteria = false;
        const detail =
          err?.error?.detail || err?.error?.message || err?.message || 'Could not save criteria.';
        this.criteriaError = detail;
        this.toastService.error('Failed to Save Criteria', detail);
      },
    });
  }

  canConfigureAgreementThreshold(): boolean {
    return this.isProjectPI() && !this.isAgreementThresholdLocked();
  }

  isAgreementThresholdLocked(): boolean {
    if (!this.project) {
      return false;
    }
    return ![
      ProjectState.SETUP,
      ProjectState.INGESTION,
      ProjectState.TITLE_ABSTRACT_SCREENING,
    ].includes(this.project.state as ProjectState);
  }

  saveAgreementThreshold(): void {
    if (!this.project || !this.canConfigureAgreementThreshold() || this.savingAgreementThreshold) {
      return;
    }

    const parsedThreshold = this.parseAgreementThresholdInput();
    if (parsedThreshold === undefined) {
      return;
    }

    const request: ProjectCreateUpdate = {
      groupId: this.project.groupId,
      title: this.project.title,
      description: this.project.description || undefined,
      exclusionCriteria: this.project.exclusionCriteria || undefined,
      cohenKappaThreshold: parsedThreshold,
    };

    this.savingAgreementThreshold = true;
    this.agreementThresholdError = '';

    this.projectService.updateProject(this.project.id, request).subscribe({
      next: (updatedProject) => {
        this.project = updatedProject;
        this.resetAgreementThresholdInput(updatedProject);
        this.savingAgreementThreshold = false;
        this.toastService.success(
          'Agreement Threshold Updated',
          updatedProject.cohenKappaThreshold === null ||
            updatedProject.cohenKappaThreshold === undefined
            ? 'The agreement threshold has been cleared.'
            : 'The agreement threshold has been saved.',
        );
      },
      error: (err) => {
        console.error('Error saving agreement threshold:', err);
        this.savingAgreementThreshold = false;
        this.agreementThresholdError =
          typeof err?.error === 'string'
            ? err.error
            : err?.error?.message || 'Could not save the agreement threshold. Please try again.';
      },
    });
  }

  resetAgreementThresholdInput(project: Project | null = this.project): void {
    this.agreementThresholdInput = project?.cohenKappaThreshold ?? null;
    this.agreementThresholdError = '';
  }

  clearAgreementThresholdInput(): void {
    this.agreementThresholdInput = '';
    this.agreementThresholdError = '';
    this.saveAgreementThreshold();
  }

  private parseAgreementThresholdInput(): number | null | undefined {
    const value = this.agreementThresholdInput;
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const threshold = Number(value);
    if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1) {
      this.agreementThresholdError = 'Threshold must be a number between 0 and 1.';
      return undefined;
    }

    return threshold;
  }

  startEditingReasons(): void {
    this.collapsedSections.update((s) => ({ ...s, exclusionReasons: false }));
    this.editableExclusionReasons = [...this.exclusionReasons];
    this.isEditingReasons = true;
    this.warningMessage = null;
    this.addReasonField();
    this.focusReason(this.editableExclusionReasons.length - 1);
  }

  defineExclusionReasons(): void {
    this.startEditingReasons();
    setTimeout(() => {
      document
        .getElementById('exclusion-reasons-section')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  cancelEditingReasons(): void {
    this.isEditingReasons = false;
    this.warningMessage = null;
  }

  addReasonField(): number {
    this.editableExclusionReasons.push('');
    return this.editableExclusionReasons.length - 1;
  }

  onReasonKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== 'Enter') {
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      this.saveExclusionReasons();
      return;
    }
    event.preventDefault();
    const newIndex = this.addReasonField();
    this.focusReason(newIndex);
  }

  focusReason(index: number): void {
    setTimeout(() => {
      const inputs = this.reasonInputs();
      if (inputs[index]) {
        inputs[index].nativeElement.focus();
      }
    });
  }

  removeReasonField(index: number): void {
    const reasonText = this.editableExclusionReasons[index]?.trim();
    if (!reasonText) {
      this.editableExclusionReasons.splice(index, 1);
      return;
    }

    this.reasonPendingRemovalIndex = index;
    this.showRemoveReasonModal = true;
  }

  confirmRemoveReason(): void {
    if (this.reasonPendingRemovalIndex !== null) {
      this.editableExclusionReasons.splice(this.reasonPendingRemovalIndex, 1);
    }
    this.showRemoveReasonModal = false;
    this.reasonPendingRemovalIndex = null;
  }

  cancelRemoveReason(): void {
    this.showRemoveReasonModal = false;
    this.reasonPendingRemovalIndex = null;
  }

  exclusionReasonError = '';

  saveExclusionReasons(): void {
    const cleaned = this.editableExclusionReasons.map((r) => r.trim()).filter((r) => r !== '');

    // Validate duplicates
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const r of cleaned) {
      if (seen.has(r)) duplicates.add(r);
      seen.add(r);
    }
    if (duplicates.size > 0) {
      this.exclusionReasonError =
        'Duplicate exclusion reasons are not allowed: "' +
        [...duplicates][0] +
        '" appears more than once.';
      return;
    }

    this.exclusionReasonError = '';

    this.projectService.setExclusionReasons(this.projectId, cleaned).subscribe({
      next: (res) => {
        this.exclusionReasons = cleaned;
        this.isEditingReasons = false;

        if (res && res.deactivatedReasons && res.deactivatedReasons.length > 0) {
          this.warningMessage = `The following exclusion reasons were already used in historical rejections and could not be fully deleted. They have been deactivated instead: ${res.deactivatedReasons.join(', ')}`;
        } else {
          this.warningMessage = null;
        }
      },
      error: (err) => {
        console.error('Error saving exclusion reasons:', err);
      },
    });
  }

  dismissWarning(): void {
    this.warningMessage = null;
  }

  getStateClass(state?: string): string {
    if (!state) {
      return 'badge-setup';
    }
    return getStageStateClass(state);
  }

  formatState(state?: string): string {
    return formatStageState(state);
  }

  cancelRemoveResearcher(): void {
    this.showRemoveConfirmModal = false;
    this.researcherToRemove = null;
  }

  loadProjectLocks(page: number = 0, q?: string): void {
    const locksSub = this.projectService
      .getProjectLocks(this.projectId, page, PAGE_SIZE, q)
      .subscribe({
        next: (res) => {
          this.projectLocks.set(res.items);
          this.projectLocksTotalItems.set(res.totalItems);
          this.projectLocksTotalPages.set(res.totalPages);
          this.projectLocksCurrentPage.set(res.currentPage);
        },
        error: (err) => {
          console.error('Error loading project locks:', err);
        },
      });
    this.sub?.add(locksSub);
  }

  getDurationString(lockedAtStr: string): string {
    if (!lockedAtStr) return '';
    const lockedAt = new Date(lockedAtStr);
    const now = new Date();
    const diffMs = now.getTime() - lockedAt.getTime();
    if (diffMs < 0) return '0m';

    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) {
      return `${diffMins}m`;
    }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
      const remainingMins = diffMins % 60;
      return `${diffHours}h ${remainingMins}m`;
    }

    const diffDays = Math.floor(diffHours / 24);
    const remainingHours = diffHours % 24;
    return `${diffDays}d ${remainingHours}h`;
  }

  getDurationBadgeClass(lockedAtStr: string): string {
    if (!lockedAtStr) return 'badge badge-neutral';
    const lockedAt = new Date(lockedAtStr);
    const now = new Date();
    const diffMs = now.getTime() - lockedAt.getTime();
    const diffHours = diffMs / 3600000;

    if (diffHours >= 48) {
      return 'badge badge-danger';
    } else if (diffHours >= 12) {
      return 'badge badge-warning';
    }
    return 'badge badge-success';
  }

  openForceUnlockConfirm(lock: any): void {
    this.lockToForceUnlock = lock;
    this.showForceUnlockConfirmModal = true;
  }

  cancelForceUnlock(): void {
    this.showForceUnlockConfirmModal = false;
    this.lockToForceUnlock = null;
  }

  confirmForceUnlock(): void {
    if (!this.lockToForceUnlock || !this.project) return;
    this.releasingLock = true;
    this.projectService.forceReleaseLock(this.project.id, this.lockToForceUnlock.id).subscribe({
      next: () => {
        this.releasingLock = false;
        this.showForceUnlockConfirmModal = false;
        this.toastService.success(
          'Lock Released',
          `The screening lock on "${this.lockToForceUnlock.paperTitle}" has been force-released.`,
        );
        this.lockToForceUnlock = null;
        this.loadProjectLocks();
      },
      error: (err) => {
        console.error('Error force-releasing lock:', err);
        this.releasingLock = false;
        this.showForceUnlockConfirmModal = false;
        this.lockToForceUnlock = null;
        this.toastService.error(
          'Failed to Release Lock',
          err?.error?.message || 'Could not force release the lock. Please try again.',
        );
      },
    });
  }

  onProtocolSave(protocol: ProjectProtocol): void {
    if (!this.project || this.savingProtocol) return;
    this.savingProtocol = true;

    const dto: ProjectProtocol = {
      ...protocol,
      searchCriteria: this.inclusionCriteriaList.join('\n'),
    };

    this.projectService.saveProjectProtocol(this.projectId, dto).subscribe({
      next: (saved) => {
        this.protocol = saved;
        this.savingProtocol = false;
        this.toastService.success('Protocol Saved', 'Protocol has been updated successfully.');
      },
      error: (err) => {
        this.savingProtocol = false;
        console.error('Error saving protocol:', err);
        const detail =
          err?.error?.detail || err?.error?.message || err?.message || 'Could not save protocol.';
        this.toastService.error('Failed to Save Protocol', detail);
      },
    });
  }
}
